import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useStockMovements } from '../../hooks/useInventory';
import { Product, StockMovement } from '../../services/inventory.service';
import { format } from 'date-fns';
import { Modal } from '../ui/Modal';
import {
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  secondaryButtonClass,
} from '../ui/FormControls';
import ProductStockSummary from './ProductStockSummary';

interface StockMovementHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

const MOVEMENT_STYLES: Record<
  string,
  { label: string; badge: string; iconWrap: string; icon: React.ElementType; sign: string }
> = {
  in: {
    label: 'In',
    badge: 'text-green-700 bg-green-50 ring-green-600/20',
    iconWrap: 'bg-green-50 text-green-600',
    icon: ArrowUpIcon,
    sign: '+',
  },
  out: {
    label: 'Out',
    badge: 'text-red-700 bg-red-50 ring-red-600/20',
    iconWrap: 'bg-red-50 text-red-600',
    icon: ArrowDownIcon,
    sign: '−',
  },
  adjustment: {
    label: 'Adjustment',
    badge: 'text-blue-700 bg-blue-50 ring-blue-600/20',
    iconWrap: 'bg-blue-50 text-blue-600',
    icon: ArrowPathIcon,
    sign: '',
  },
};

const FALLBACK_STYLE = {
  label: 'Movement',
  badge: 'text-gray-700 bg-gray-50 ring-gray-500/20',
  iconWrap: 'bg-gray-100 text-gray-600',
  icon: ArrowPathIcon,
  sign: '',
};

const formatReason = (reason: string) => {
  return reason
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const StockMovementHistoryModal: React.FC<StockMovementHistoryModalProps> = ({
  isOpen,
  onClose,
  product,
}) => {
  const { data: movements = [], isLoading, error } = useStockMovements(
    isOpen ? product?.id || '' : '',
    100
  );

  if (!product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Stock Movement History"
      description="The most recent 100 movements for this product."
      size="xl"
    >
      <div className="space-y-5">
        <ProductStockSummary product={product} />

        <div className="max-h-[28rem] overflow-y-auto pr-1">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading movement history...</p>
            </div>
          ) : error ? (
            <p className="py-12 text-center text-sm text-red-600">Error loading movement history</p>
          ) : movements.length === 0 ? (
            <div className="py-12 text-center">
              <ClockIcon className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm font-medium text-gray-900">No stock movements yet</p>
              <p className="mt-1 text-sm text-gray-500">
                Receipts, issues and adjustments for this product will appear here.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {movements.map((movement: StockMovement) => {
                const style = MOVEMENT_STYLES[movement.type] || FALLBACK_STYLE;
                const Icon = style.icon;
                return (
                  <li key={movement.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start gap-3">
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${style.iconWrap}`}>
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`${badgeClass} ${style.badge}`}>{style.label}</span>
                            <span className="text-sm font-medium text-gray-900">
                              {formatReason(movement.reason)}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {format(new Date(movement.createdAt), 'dd MMM yyyy, hh:mm a')}
                          </span>
                        </div>

                        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                          <div>
                            <dt className="text-xs text-gray-500">Quantity</dt>
                            <dd className="font-semibold text-gray-900 tabular-nums">
                              {style.sign}
                              {formatNumber(movement.quantity)} {product.unit}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Stock change</dt>
                            <dd className="font-medium text-gray-900 tabular-nums">
                              {formatNumber(movement.previousStock)} → {formatNumber(movement.newStock)}
                            </dd>
                          </div>
                          {movement.unitCost != null && Number(movement.unitCost) > 0 && (
                            <div>
                              <dt className="text-xs text-gray-500">Unit cost</dt>
                              <dd className="font-medium text-gray-900 tabular-nums">
                                {formatMoney(Number(movement.unitCost))}
                              </dd>
                            </div>
                          )}
                          {movement.totalValue != null && Number(movement.totalValue) > 0 && (
                            <div>
                              <dt className="text-xs text-gray-500">Total value</dt>
                              <dd className="font-medium text-gray-900 tabular-nums">
                                {formatMoney(Number(movement.totalValue))}
                              </dd>
                            </div>
                          )}
                          {movement.referenceType && (
                            <div>
                              <dt className="text-xs text-gray-500">Reference</dt>
                              <dd className="font-medium capitalize text-gray-900">
                                {movement.referenceType.replace(/_/g, ' ')}
                              </dd>
                            </div>
                          )}
                          {movement.location && (
                            <div>
                              <dt className="text-xs text-gray-500">Location</dt>
                              <dd className="font-medium text-gray-900">{movement.location}</dd>
                            </div>
                          )}
                        </dl>

                        {movement.notes && (
                          <p className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                            {movement.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
      </FormActions>
    </Modal>
  );
};

export default StockMovementHistoryModal;
